import React, { useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import styled, { css } from "styled-components";

import "../css/myclimate-radio.scss";
import { SortableContainer, SortableElement } from "react-sortable-hoc";

const context = require.context("../../assets/images/quiz/", true, /.svg$/);

const images = {};
context.keys().forEach((key) => {
  const filename = key
    .split("./")
    .pop()
    .substring(0, key.length - 6); // remove the file extension
  images[filename] = context(key);
});

const CorrectComment = withTranslation()(({ t, translationKey }) => (
  <div
    style={{
      color: "#6EAA28",
      fontWeight: "bold",
      marginLeft: "1.75rem",
    }}
  >
    {t(translationKey)}
  </div>
));

const WrongComment = withTranslation()(({ t, translationKey }) => (
  <div
    style={{
      color: "#e6007e",
      fontWeight: "bold",
      marginLeft: "1.75rem",
    }}
  >
    {t(translationKey)}
  </div>
));

const EmptyComment = () => <div style={{ fontWeight: "bold" }}>&nbsp;</div>;

const Comments = ({ completed, answerId, question, givenAnswers }) => {
  if (completed && isCorrect(givenAnswers, question, answerId)) {
    return <CorrectComment translationKey="quiz.correct" />;
  } else if (
    completed &&
    isWrong(givenAnswers, question, answerId) &&
    question.correctAnswers.includes(answerId)
  ) {
    return <CorrectComment translationKey="quiz.correct_answer" />;
  } else if (
    completed &&
    givenAnswers.includes(answerId) &&
    !question.correctAnswers.includes(answerId)
  ) {
    return <WrongComment translationKey="quiz.wrong" />;
  } else {
    return <EmptyComment />;
  }
};

const SortableList = styled.ul`
  list-style-type: none;
`;

const SortableItem = styled.li`
  list-style-type: none;
  user-select: none;
  cursor: grab;
  pointer-events: auto !important;

  background: white;

  display: flex;
  justify-content: space-between;
  alignitems: top;

  padding: 1rem;
  border: 1px solid #d0d0d0;
  margin-bottom: 0.25rem;
`;

const SortableDragHandle = () => (
  <div style={{ height: "1rem" }}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#B2B2B2"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="1"></circle>
      <circle cx="12" cy="5" r="1"></circle>
      <circle cx="12" cy="19" r="1"></circle>

      <circle cx="19" cy="12" r="1"></circle>
      <circle cx="19" cy="5" r="1"></circle>
      <circle cx="19" cy="19" r="1"></circle>
    </svg>
  </div>
);

const ArrowUp = styled.div`
  &::before {
    font-family: "myclimate-icons";
    content: "\\e828";
    font-weight: bold;
    font-size: 1.25rem;

    ${(props) =>
      props.hide &&
      css`
        visibility: hidden;
      `}
  }
`;

const ArrowDown = styled.div`
  &::before {
    font-family: "myclimate-icons";
    content: "\\e82a";
    font-weight: bold;
    font-size: 1.25rem;

    ${(props) =>
      props.hide &&
      css`
        visibility: hidden;
      `}
  }
`;

const SortableAnswer = SortableElement(
  ({
    answer,
    completed,
    correct,
    correctIndex,
    moveUp,
    moveDown,
    first,
    last,
  }) => (
    <SortableItem>
      <div
        style={{ display: "flex", alignItems: "center" }}
        className="noselect"
      >
        {!completed && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div onClick={() => moveUp()}>
              <ArrowUp hide={first} />
            </div>
            <div onClick={() => moveDown()}>
              <ArrowDown hide={last} />
            </div>
          </div>
        )}
        {completed && (
          <div
            style={{
              color: correct ? "#6EAA28" : "#e6007e",
              fontWeight: "bold",
              width: "1.5rem",
              minWidth: "1.5rem",
              textAlign: "center",
            }}
          >
            {correctIndex + 1}
          </div>
        )}
        <div style={{ marginLeft: "1rem", lineHeight: "1.9rem" }}>{answer}</div>
      </div>
      <div>
        {completed && correct && (
          <CorrectComment translationKey="quiz.correct" />
        )}
        {completed && !correct && <WrongComment translationKey="quiz.wrong" />}
      </div>
    </SortableItem>
  )
);
const array_move = (arr, old_index, new_index) => {
  if (new_index >= arr.length) {
    var k = new_index - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  return arr; // for testing
};
const SortableAnswers = withTranslation()(
  ({ t, possibleAnswers, questionId, setAnswers, completed, question }) => (
    <SortableAnswersWithTranslations
      t={t}
      possibleAnswers={possibleAnswers}
      questionId={questionId}
      question={question}
      onSortEnd={({ oldIndex, newIndex }) => {
        setAnswers(questionId, array_move(possibleAnswers, oldIndex, newIndex));
      }}
      distance={1}
      completed={completed}
      shouldCancelStart={() => completed}
      setAnswers={setAnswers}
    />
  )
);
const SortableAnswersWithTranslations = SortableContainer(
  ({ t, possibleAnswers, questionId, completed, setAnswers, question }) => (
    <SortableList>
      {possibleAnswers.map((answerId, index) => (
        <SortableAnswer
          key={answerId}
          index={index}
          answer={t(`quiz.questions.${questionId}.answers.${answerId}`)}
          correct={question.correctAnswers[index] === answerId}
          completed={completed}
          correctIndex={question.correctAnswers.indexOf(answerId)}
          moveUp={() => {
            console.log("up");
            setAnswers(
              questionId,
              array_move(possibleAnswers, index, index > 0 ? index - 1 : index)
            );
          }}
          moveDown={() =>
            setAnswers(
              questionId,
              array_move(
                possibleAnswers,
                index,
                index < possibleAnswers.length - 1 ? index + 1 : index
              )
            )
          }
          first={index === 0}
          last={index === possibleAnswers.length - 1}
        />
      ))}
    </SortableList>
  )
);

const QuestionTypeHelpTextWrapper = styled.div`
  font-size: 0.75rem;
  margin-top: 1rem;
`;

const QuestionTypeHelpText = withTranslation()(({ t, type }) => {
  switch (type) {
    case "single":
      return (
        <QuestionTypeHelpTextWrapper>
          {t("quiz.single_choice_help")}
        </QuestionTypeHelpTextWrapper>
      );
    case "multiple":
      return (
        <QuestionTypeHelpTextWrapper>
          {t("quiz.multiple_choice_help")}
        </QuestionTypeHelpTextWrapper>
      );
    case "sort":
      return (
        <QuestionTypeHelpTextWrapper>
          {t("quiz.sort_help")}
        </QuestionTypeHelpTextWrapper>
      );
  }
});

const isCorrect = (givenAnswers, question, answerId) => {
  return (
    givenAnswers.includes(answerId) &&
    question.correctAnswers.includes(answerId)
  );
};

const isWrong = (givenAnswers, question, answerId) => {
  return (
    !givenAnswers.includes(answerId) &&
    question.correctAnswers.includes(answerId)
  );
};

const checkAnswer = (
  question,
  addAnswer,
  completed,
  givenAnswers,
  choiceId,
  checked
) => {
  if (completed) {
    return;
  }

  let answers = givenAnswers || [];

  if (checked) {
    if (question.type === "single") {
      answers = [choiceId];
    } else {
      answers.push(choiceId);
    }
  } else {
    answers = givenAnswers.filter((answer) => answer !== choiceId);
  }

  addAnswer(question.id, answers);
};

const QuizQuestion = ({
  t,
  i18n,
  question,
  next,
  previous,
  addAnswer,
  givenAnswers,
  completed,
  setCompleted,
}) => {
  const imageRef = useRef(null);

  useEffect(() => {
    if (!completed && givenAnswers.length <= 0) {
      imageRef.current.scrollIntoView();
    }
  });

  return (
    <div ref={imageRef} style={{ paddingTop: "4.5rem" }}>
      {question.image && (
        <img src={images[question.image].default} style={{ width: "75%" }} />
      )}
      <div
        style={{ fontWeight: "bold", color: "#009FE3", marginTop: "1.5rem" }}
      >
        {t(`quiz.questions.${question.id}.title`)}
      </div>
      {i18n.exists(`quiz.questions.${question.id}.subtitle`) && (
        <div style={{ marginTop: "1rem" }}>
          {t(`quiz.questions.${question.id}.subtitle`)}
        </div>
      )}
      <QuestionTypeHelpText type={question.type} />
      <div style={{ marginTop: "2rem" }}>
        {question.type === "sort" && (
          <>
            <div style={{ fontSize: "0.9rem", marginBottom: "1rem" }}>
              {t(`quiz.questions.${question.id}.sort_info.top`)}
            </div>
            <SortableAnswers
              possibleAnswers={question.answers}
              questionId={question.id}
              setAnswers={addAnswer}
              completed={completed}
              question={question}
            />
            <div style={{ fontSize: "0.9rem", marginTop: "1rem" }}>
              {t(`quiz.questions.${question.id}.sort_info.bottom`)}
            </div>
          </>
        )}
        {question.type !== "sort" &&
          question.answers.map((answerId) => (
            <div key={answerId} className="form-check">
              {question.type === "single" && (
                <input
                  type="radio"
                  name="answer"
                  id={answerId}
                  value={answerId}
                  className="form-check-input"
                  checked={givenAnswers.includes(answerId)}
                  onChange={(event) =>
                    checkAnswer(
                      question,
                      addAnswer,
                      completed,
                      givenAnswers,
                      answerId,
                      event.target.checked
                    )
                  }
                />
              )}
              {question.type === "multiple" && (
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={answerId}
                  checked={givenAnswers.includes(answerId) || false}
                  onChange={(event) =>
                    checkAnswer(
                      question,
                      addAnswer,
                      completed,
                      givenAnswers,
                      answerId,
                      event.target.checked
                    )
                  }
                />
              )}
              <label
                htmlFor={answerId}
                className="form-check-label"
                onChange={() =>
                  checkAnswer(
                    question,
                    addAnswer,
                    completed,
                    givenAnswers,
                    answerId
                  )
                }
              >
                {t(`quiz.questions.${question.id}.answers.${answerId}`)}
              </label>
              <Comments
                completed={completed}
                answerId={answerId}
                question={question}
                givenAnswers={givenAnswers}
              />
            </div>
          ))}
      </div>
      {completed && (
        <div style={{ marginTop: "1.5rem", color: "#009FE3" }}>
          {t(`quiz.questions.${question.id}.explanation`)}
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "3rem",
        }}
      >
        <button className="btn btn-primary" onClick={() => previous()}>
          {t("quiz.previous")}
        </button>
        <button
          className="btn btn-info"
          onClick={() => (completed ? next() : setCompleted(question.id))}
        >
          {t(completed ? "quiz.next" : "quiz.solve")}
        </button>
      </div>
    </div>
  );
};

export default withTranslation()(QuizQuestion);
